import React from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar as CalendarIcon,
  Check,
  Instagram,
  Facebook,
  Mail,
  Loader2,
  Sparkles,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { api, KYVIXE } from "@/lib/api";

const SERVICES = [
  "Websites",
  "AI Chatbots",
  "Automations",
  "Bookings & Reservations",
  "Other",
];

function todayLocal() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function BookingForm() {
  const [selectedDate, setSelectedDate] = React.useState(undefined);
  const [slots, setSlots] = React.useState([]);
  const [slotsLoading, setSlotsLoading] = React.useState(false);
  const [selectedTime, setSelectedTime] = React.useState("");
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: SERVICES[0],
    notes: "",
    website: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(null);

  const disableDay = (d) => {
    if (!d) return true;
    const t = todayLocal();
    if (d < t) return true;
    const dow = d.getDay(); // 0 Sun .. 6 Sat
    return dow === 0 || dow === 6;
  };

  React.useEffect(() => {
    if (!selectedDate) return;
    setSelectedTime("");
    setSlotsLoading(true);
    api
      .get("/bookings/availability", { params: { date: formatDate(selectedDate) } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      toast.error("Pick a date and time first.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        date: formatDate(selectedDate),
        time: selectedTime,
      };
      const r = await api.post("/bookings", payload);
      setConfirmed({ ...payload, id: r.data.id });
      toast.success("Booking confirmed. See you soon!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save booking.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="kyv-card p-10 text-center" data-testid="booking-confirmation">
        <div className="p-3 rounded-full bg-blue-500/10 text-blue-400 w-fit mx-auto mb-4">
          <Check size={22} />
        </div>
        <h3 className="font-display lowercase font-bold text-3xl tracking-tight">
          you're <span className="text-blue-500">booked.</span>
        </h3>
        <p className="mt-3 text-gray-300">
          {confirmed.name}, we've locked in {confirmed.date} at {confirmed.time}.
          A confirmation is on its way to <span className="text-blue-400">{confirmed.email}</span>.
        </p>
        <button
          onClick={() => setConfirmed(null)}
          className="mt-6 kyv-btn-ghost"
          data-testid="booking-book-another-btn"
        >
          book another slot
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="booking-form">
      <div className="kyv-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarIcon size={18} className="text-blue-400" />
          <h3 className="font-display lowercase font-bold text-xl">1. pick a date</h3>
        </div>
        <div className="flex justify-center">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            disabled={disableDay}
            className="rounded-xl border border-white/10 bg-[#12121a] p-2"
            data-testid="booking-calendar"
          />
        </div>
        <p className="text-xs text-gray-500 mt-3 text-center">
          available mon–fri, 9:00 – 18:00 · 30-minute slots
        </p>
      </div>

      <div className="space-y-6">
        <div className="kyv-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={18} className="text-blue-400" />
            <h3 className="font-display lowercase font-bold text-xl">2. pick a time</h3>
          </div>
          {!selectedDate && (
            <p className="text-sm text-gray-500">choose a date to see open slots.</p>
          )}
          {selectedDate && slotsLoading && (
            <p className="text-sm text-gray-500 inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> loading slots…
            </p>
          )}
          {selectedDate && !slotsLoading && slots.length === 0 && (
            <p className="text-sm text-gray-500">no slots left for this day. pick another date.</p>
          )}
          {selectedDate && !slotsLoading && slots.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {slots.map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => setSelectedTime(t)}
                  className={`text-sm py-2 rounded-full border transition ${
                    selectedTime === t
                      ? "bg-blue-600 border-blue-500 text-white"
                      : "bg-white/5 border-white/10 text-gray-200 hover:border-blue-500/60 hover:text-blue-300"
                  }`}
                  data-testid={`booking-slot-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="kyv-card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-blue-400" />
            <h3 className="font-display lowercase font-bold text-xl">3. your details</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="name" name="name" value={form.name} setForm={setForm} required />
            <Input label="email" name="email" type="email" value={form.email} setForm={setForm} required />
            <Input label="phone" name="phone" value={form.phone} setForm={setForm} />
            <Input label="company" name="company" value={form.company} setForm={setForm} />
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-400 lowercase">service</label>
              <select
                value={form.service}
                onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
                className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60"
                data-testid="booking-service-select"
              >
                {SERVICES.map((s) => (
                  <option key={s} value={s} className="bg-[#12121a]">
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-400 lowercase">notes (optional)</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60"
                data-testid="booking-notes"
                placeholder="tell us what you want to solve…"
              />
            </div>
            <input
              type="text"
              value={form.website}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="kyv-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="booking-submit-btn"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />} confirm booking
          </button>
        </div>
      </div>
    </form>
  );
}

function Input({ label, name, value, setForm, type = "text", required, textarea }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="text-xs text-gray-400 lowercase">{label}{required && " *"}</label>
      <Comp
        type={type}
        value={value}
        onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
        required={required}
        className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60"
        data-testid={`booking-input-${name}`}
      />
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: SERVICES[0],
    message: "",
    website: "",
  });
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent. We'll reply within 24h.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="kyv-card p-10 text-center" data-testid="contact-confirmation">
        <div className="p-3 rounded-full bg-blue-500/10 text-blue-400 w-fit mx-auto mb-4">
          <Check size={22} />
        </div>
        <h3 className="font-display lowercase font-bold text-3xl">
          got it. <span className="text-blue-500">we're on it.</span>
        </h3>
        <p className="mt-3 text-gray-300">
          you'll hear back at {form.email} within 24 hours.
        </p>
        <button
          onClick={() => {
            setSent(false);
            setForm({ name: "", email: "", phone: "", company: "", service: SERVICES[0], message: "", website: "" });
          }}
          className="mt-6 kyv-btn-ghost"
          data-testid="contact-send-another-btn"
        >
          send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="kyv-card p-6 space-y-4" data-testid="contact-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="name" name="name" value={form.name} setForm={setForm} required />
        <Input label="email" name="email" type="email" value={form.email} setForm={setForm} required />
        <Input label="phone" name="phone" value={form.phone} setForm={setForm} />
        <Input label="company" name="company" value={form.company} setForm={setForm} />
        <div className="sm:col-span-2">
          <label className="text-xs text-gray-400 lowercase">service interested in</label>
          <select
            value={form.service}
            onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
            className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60"
            data-testid="contact-service-select"
          >
            {SERVICES.map((s) => (
              <option key={s} value={s} className="bg-[#12121a]">{s}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-gray-400 lowercase">message *</label>
          <textarea
            rows={5}
            required
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/60"
            data-testid="contact-message"
            placeholder="tell us about your business and what you'd like to automate…"
          />
        </div>
      </div>
      <input
        type="text"
        value={form.website}
        onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />
      <button
        type="submit"
        disabled={loading}
        className="kyv-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="contact-submit-btn"
      >
        {loading && <Loader2 size={16} className="animate-spin" />} send message
      </button>
    </form>
  );
}

export default function Contact() {
  const { hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);

  return (
    <div className="bg-section-glow" data-testid="contact-page">
      <section className="pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="font-display lowercase font-bold text-5xl sm:text-6xl tracking-tighter" data-testid="contact-heading">
            let's build something <span className="text-blue-500">that ships.</span>
          </h1>
          <p className="mt-5 text-gray-400 max-w-2xl">
            book a 30-minute call with our team, or send us a message and we'll
            get back within a business day.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href={`mailto:${KYVIXE.email}`}
              className="kyv-badge hover:text-blue-300"
              data-testid="contact-email-badge"
            >
              <Mail size={14} /> {KYVIXE.email}
            </a>
            <a href={KYVIXE.instagram} target="_blank" rel="noreferrer" className="kyv-badge hover:text-blue-300" data-testid="contact-instagram-badge">
              <Instagram size={14} /> instagram
            </a>
            <a href={KYVIXE.facebook} target="_blank" rel="noreferrer" className="kyv-badge hover:text-blue-300" data-testid="contact-facebook-badge">
              <Facebook size={14} /> facebook
            </a>
          </div>
        </div>
      </section>

      <section id="book" className="pt-8 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-6">
              <h2 className="font-display lowercase font-bold text-3xl sm:text-4xl tracking-tight">
                book a call
              </h2>
              <p className="text-gray-400 mt-2 max-w-xl">
                mon–fri, 30-min discovery call. we'll review your business and
                map out the fastest win.
              </p>
            </div>
            <BookingForm />
          </motion.div>
        </div>
      </section>

      <section id="contact" className="pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-6">
              <h2 className="font-display lowercase font-bold text-3xl sm:text-4xl tracking-tight">
                or send us a message
              </h2>
              <p className="text-gray-400 mt-2">
                every message lands directly in our inbox at{" "}
                <span className="text-blue-400">{KYVIXE.email}</span>.
              </p>
            </div>
            <ContactForm />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
